import React, { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
	FaArrowRight,
	FaCheckCircle,
	FaChevronDown,
	FaChevronUp,
	FaCommentDots,
	FaEnvelope,
	FaFileAlt,
	FaMagic,
	FaPaperPlane,
	FaSearch,
	FaSyncAlt,
	FaTimes,
} from 'react-icons/fa';
import { useHistory, useLocation } from 'react-router-dom';
import { acknowledgeActivityNotifications, getAllActivityNotifications } from './api';
import { activityNotificationPath, isActivityPopupSuppressedPath } from './navigation';
import {
	ActivityAcknowledgeReason,
	ActivityNotification,
	ActivityNotificationDetail,
} from './types';
import './ActivityNotificationHost.css';

const POLL_INTERVAL_MS = 15000;
const LIVE_DISPLAY_MS = 10000;

const notificationIcon = (eventType: string): ReactNode => {
	switch (eventType) {
		case 'AUTO_ANALYSIS_COMPLETED':
		case 'AUTO_ANALYSIS_TRADE_SELECTION_COMPLETED':
		case 'AUTO_ANALYSIS_TRADE_SELECTION_FAILED':
		case 'DRAWING_INGESTION_COMPLETED':
		case 'SCOPE_GAP_ANALYSIS_COMPLETED':
			return <FaSearch />;
		case 'BID_INVITES_SENT':
			return <FaEnvelope />;
		case 'BID_LIST_AUDIT_READY':
			return <FaMagic />;
		case 'SCOPE_CHECKLIST_REFRESH_NEEDED':
			return <FaSyncAlt />;
		case 'BID_SUBMITTED':
		case 'BID_REVISED':
			return <FaPaperPlane />;
		case 'PROJECT_MESSAGE_RECEIVED':
			return <FaCommentDots />;
		case 'CHANGE_LOG_COMPLETED':
		case 'CONTRACT_INCLUSION_COMPLETED':
			return <FaFileAlt />;
		default:
			return <FaCheckCircle />;
	}
};

const formatActivityTime = (value: string) => {
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	return new Intl.DateTimeFormat(undefined, {
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		month: 'short',
	}).format(date);
};

const ActivityDetails: FC<{ details: ActivityNotificationDetail[] }> = ({ details }) => (
	<div className="activity-notification-details" aria-label="Scope changes">
		{details.map((detail, index) => (
			<div
				className={`activity-notification-detail activity-notification-detail--${detail.kind.toLowerCase()}`}
				key={`${detail.kind}-${index}-${detail.text}`}
			>
				<span className="activity-notification-detail__mark">
					{detail.kind === 'ADDED' ? 'Added' : 'Deleted'}
				</span>
				<span>{detail.text}</span>
			</div>
		))}
	</div>
);

interface NotificationActionsProps {
	expanded: boolean;
	notification: ActivityNotification;
	onToggleDetails: () => void;
	onView: () => void;
}

const NotificationActions: FC<NotificationActionsProps> = ({
	expanded,
	notification,
	onToggleDetails,
	onView,
}) => {
	const path = activityNotificationPath(notification);
	const hasDetails = notification.details.length > 0;
	if (!path && !hasDetails) return null;
	return (
		<div className="activity-notification-card__actions">
			{hasDetails && (
				<button className="activity-notification-button" onClick={onToggleDetails} type="button">
					{expanded ? <FaChevronUp /> : <FaChevronDown />}
					{expanded ? 'Hide changes' : 'View changes'}
				</button>
			)}
			{path && (
				<button
					className="activity-notification-button activity-notification-button--primary"
					onClick={onView}
					type="button"
				>
					View
					<FaArrowRight />
				</button>
			)}
		</div>
	);
};

interface LiveCardProps {
	expanded: boolean;
	notification: ActivityNotification;
	onClose: () => void;
	onToggleDetails: () => void;
	onView: () => void;
}

const LiveCard: FC<LiveCardProps> = ({
	expanded,
	notification,
	onClose,
	onToggleDetails,
	onView,
}) => (
	<div
		aria-atomic="true"
		aria-live="polite"
		className={`activity-notification-card${
			notification.isRevision ? ' activity-notification-card--revision' : ''
		}`}
		role="status"
	>
		<div className="activity-notification-card__body">
			<div className="activity-notification-card__header">
				<span className="activity-notification-card__icon" aria-hidden="true">
					{notificationIcon(notification.eventType)}
				</span>
				<div className="activity-notification-card__heading">
					<div className="activity-notification-card__eyebrow">
						<span title={notification.projectName}>{notification.projectName}</span>
						{notification.isRevision && (
							<strong className="activity-notification-card__revision">Revision</strong>
						)}
					</div>
					<h3>{notification.title}</h3>
				</div>
				<button
					aria-label="Dismiss notification"
					className="activity-notification-card__close"
					onClick={onClose}
					type="button"
				>
					<FaTimes />
				</button>
			</div>
			<p>{notification.message}</p>
			{expanded && notification.details.length > 0 && (
				<ActivityDetails details={notification.details} />
			)}
			<NotificationActions
				expanded={expanded}
				notification={notification}
				onToggleDetails={onToggleDetails}
				onView={onView}
			/>
		</div>
	</div>
);

interface DigestProps {
	expandedIds: Set<number>;
	notifications: ActivityNotification[];
	onDismiss: () => void;
	onToggleDetails: (recipientId: number) => void;
	onView: (notification: ActivityNotification) => void;
}

const Digest: FC<DigestProps> = ({
	expandedIds,
	notifications,
	onDismiss,
	onToggleDetails,
	onView,
}) => {
	const groups = useMemo(() => {
		const result: Record<string, ActivityNotification[]> = {};
		notifications.forEach((notification) => {
			const key = notification.projectName || 'Project';
			if (!result[key]) result[key] = [];
			result[key].push(notification);
		});
		return result;
	}, [notifications]);

	return (
		<div
			aria-label="Completed ScrubPlan activities"
			className="activity-notification-card activity-notification-digest"
			role="dialog"
		>
			<div className="activity-notification-card__body">
				<div className="activity-notification-card__header activity-notification-digest__summary">
					<span className="activity-notification-card__icon" aria-hidden="true">
						<FaCheckCircle />
					</span>
					<div className="activity-notification-card__heading">
						<div className="activity-notification-card__eyebrow">
							<span>Activity update</span>
						</div>
						<h3>
							{notifications.length} completed{' '}
							{notifications.length === 1 ? 'activity' : 'activities'}
						</h3>
						<p>Here is what finished while you were away.</p>
					</div>
					<button
						aria-label="Dismiss all completed activities"
						className="activity-notification-card__close"
						onClick={onDismiss}
						type="button"
					>
						<FaTimes />
					</button>
				</div>
				<div className="activity-notification-digest__scroll">
					{Object.keys(groups).map((projectName) => (
						<section className="activity-notification-project-group" key={projectName}>
							<h4>{projectName}</h4>
							{groups[projectName].map((notification) => {
								const expanded = expandedIds.has(notification.recipientId);
								return (
									<article
										className="activity-notification-digest-item"
										key={notification.recipientId}
									>
										<div className="activity-notification-digest-item__top">
											<span className="activity-notification-digest-item__icon" aria-hidden="true">
												{notificationIcon(notification.eventType)}
											</span>
											<div className="activity-notification-digest-item__copy">
												<strong>
													{notification.title}
													{notification.isRevision ? ' ? Revision' : ''}
												</strong>
												<p>{notification.message}</p>
											</div>
											<time
												className="activity-notification-digest-item__time"
												dateTime={notification.occurredAt}
											>
												{formatActivityTime(notification.occurredAt)}
											</time>
										</div>
										{expanded && notification.details.length > 0 && (
											<ActivityDetails details={notification.details} />
										)}
										<div className="activity-notification-digest-item__actions">
											{notification.details.length > 0 && (
												<button
													className="activity-notification-button"
													onClick={() => onToggleDetails(notification.recipientId)}
													type="button"
												>
													{expanded ? <FaChevronUp /> : <FaChevronDown />}
													{expanded ? 'Hide changes' : 'View changes'}
												</button>
											)}
											{activityNotificationPath(notification) && (
												<button
													className="activity-notification-button activity-notification-button--primary"
													onClick={() => onView(notification)}
													type="button"
												>
													View
													<FaArrowRight />
												</button>
											)}
										</div>
									</article>
								);
							})}
						</section>
					))}
				</div>
			</div>
		</div>
	);
};

const ActivityNotificationHost: FC = () => {
	const history = useHistory();
	const location = useLocation();
	const seenRecipientIds = useRef<Set<number>>(new Set());
	const [digest, setDigest] = useState<ActivityNotification[]>([]);
	const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
	const [initialFetchComplete, setInitialFetchComplete] = useState(false);
	const [liveQueue, setLiveQueue] = useState<ActivityNotification[]>([]);
	const suppressed = isActivityPopupSuppressedPath(location.pathname);
	const liveNotification = liveQueue.length ? liveQueue[0] : null;
	const liveRecipientId = liveNotification ? liveNotification.recipientId : 0;

	const acknowledge = (recipientIds: number[], reason: ActivityAcknowledgeReason) => {
		if (!recipientIds.length) return;
		acknowledgeActivityNotifications(recipientIds, reason).catch(() => undefined);
	};

	useEffect(() => {
		let active = true;
		getAllActivityNotifications()
			.then((notifications) => {
				if (!active) return;
				notifications.forEach((notification) =>
					seenRecipientIds.current.add(notification.recipientId),
				);
				setDigest(notifications);
			})
			.catch(() => undefined)
			.then(() => {
				if (active) setInitialFetchComplete(true);
			});
		return () => {
			active = false;
		};
	}, []);

	useEffect(() => {
		if (!initialFetchComplete) return undefined;
		let active = true;
		const poll = () => {
			if (document.visibilityState === 'hidden') return;
			getAllActivityNotifications()
				.then((notifications) => {
					if (!active) return;
					const fresh = notifications.filter(
						(notification) => !seenRecipientIds.current.has(notification.recipientId),
					);
					fresh.forEach((notification) => seenRecipientIds.current.add(notification.recipientId));
					if (fresh.length) setLiveQueue((current) => current.concat(fresh));
				})
				.catch(() => undefined);
		};
		const intervalId = window.setInterval(poll, POLL_INTERVAL_MS);
		const handleVisibility = () => {
			if (document.visibilityState === 'visible') poll();
		};
		document.addEventListener('visibilitychange', handleVisibility);
		return () => {
			active = false;
			window.clearInterval(intervalId);
			document.removeEventListener('visibilitychange', handleVisibility);
		};
	}, [initialFetchComplete]);

	useEffect(() => {
		if (!liveRecipientId || suppressed || digest.length > 0) return undefined;
		const timeoutId = window.setTimeout(() => {
			setLiveQueue((current) =>
				current.filter((notification) => notification.recipientId !== liveRecipientId),
			);
		}, LIVE_DISPLAY_MS);
		return () => window.clearTimeout(timeoutId);
	}, [digest.length, liveRecipientId, suppressed]);

	const toggleDetails = (recipientId: number) => {
		setExpandedIds((current) => {
			const next = new Set(current);
			if (next.has(recipientId)) next.delete(recipientId);
			else next.add(recipientId);
			return next;
		});
	};

	const viewNotification = (notification: ActivityNotification, source: 'DIGEST' | 'LIVE') => {
		const path = activityNotificationPath(notification);
		acknowledge([notification.recipientId], 'OPENED');
		if (source === 'DIGEST') {
			setDigest((current) =>
				current.filter((item) => item.recipientId !== notification.recipientId),
			);
		} else {
			setLiveQueue((current) =>
				current.filter((item) => item.recipientId !== notification.recipientId),
			);
		}
		if (path) history.push(path);
	};

	if (suppressed || (!digest.length && !liveNotification)) return null;

	return (
		<div className="activity-notification-host">
			{digest.length > 0 ? (
				<Digest
					expandedIds={expandedIds}
					notifications={digest}
					onDismiss={() => {
						acknowledge(
							digest.map((notification) => notification.recipientId),
							'DISMISSED',
						);
						setDigest([]);
					}}
					onToggleDetails={toggleDetails}
					onView={(notification) => viewNotification(notification, 'DIGEST')}
				/>
			) : (
				liveNotification && (
					<LiveCard
						expanded={expandedIds.has(liveNotification.recipientId)}
						notification={liveNotification}
						onClose={() => {
							acknowledge([liveNotification.recipientId], 'DISMISSED');
							setLiveQueue((current) =>
								current.filter((item) => item.recipientId !== liveNotification.recipientId),
							);
						}}
						onToggleDetails={() => toggleDetails(liveNotification.recipientId)}
						onView={() => viewNotification(liveNotification, 'LIVE')}
					/>
				)
			)}
		</div>
	);
};

export default ActivityNotificationHost;
